'use client';

import { useState, type ReactNode } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import { signOut } from 'next-auth/react';
import { LogOutIcon, Settings } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuItem } from '@/components/ui/sidebar';
import { Skeleton } from '@/components/ui/skeleton';
import { LogoutDialog } from '@/components/logout-dialog/logout-dialog';
import { useNavigationLoading } from '@/lib/contexts/navigation-loading-context';
import { cn } from '@/lib/utils';

interface LawFirmHeaderProps {
  /**
   * Optional logo component to display
   */
  logo?: ReactNode;
  /**
   * Law firm name to display
   */
  firmName?: string;
  /**
   * Whether the branding is loading
   */
  isLoading?: boolean;
  /**
   * Optional callback when user clicks a menu item
   */
  onMenuItemClick?: (action: 'account' | 'logout') => void;
  /**
   * Optional custom menu items to add before the logout item
   */
  customMenuItems?: ReactNode;
}

/**
 * Law firm header component with profile dropdown menu
 * Displays law firm branding (logo + name) as a dropdown trigger
 * with user account actions
 */
export function LawFirmHeader({
  logo,
  firmName = 'Vale',
  isLoading = false,
  onMenuItemClick,
  customMenuItems
}: LawFirmHeaderProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const { startNavigation } = useNavigationLoading();

  const handleMenuItemClick = (action: 'account' | 'logout') => {
    const isOnAccountPage = pathname.startsWith('/account');

    switch (action) {
      case 'account':
        if (!isOnAccountPage) {
          startNavigation('Loading account settings...');
        }
        router.push('/account/profile');
        break;
      case 'logout':
        setIsLoggingOut(true);
        // Small delay to show logout dialog
        setTimeout(async () => {
          await signOut({ callbackUrl: '/login' });
        }, 500);
        break;
    }

    onMenuItemClick?.(action);
  };

  const handleHomeClick = () => {
    if (pathname !== '/all' && pathname !== '/') {
      startNavigation('Loading matters...');
    }
  };

  const navLinkClasses = cn(
    'flex items-center gap-2 rounded-md p-1.5',
    'hover:bg-gray-100 dark:hover:bg-gray-800',
    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
    'transition-colors'
  );

  // Show skeleton if loading
  if (isLoading) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex items-center gap-2 p-1.5">
            <Skeleton
              variant="circle"
              width={20}
              height={20}
              className="animate-pulse"
            />
            <Skeleton
              variant="text"
              width={120}
              height={20}
              className="animate-pulse"
            />
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <>
      <LogoutDialog open={isLoggingOut} />
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex w-full items-center gap-1">
            <Link
              href="/all"
              onClick={handleHomeClick}
              aria-label="All matters"
              className={cn(navLinkClasses, 'min-w-0 flex-1')}
            >
              {logo}
              <span className="flex-1 truncate text-left text-base font-semibold">
                {firmName}
              </span>
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="Open account menu"
                  className={cn(
                    'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                    'hover:bg-gray-100 dark:hover:bg-gray-800',
                    'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
                    'data-[state=open]:bg-gray-100 dark:data-[state=open]:bg-gray-800',
                    'transition-colors'
                  )}
                >
                  <Settings className="h-5 w-5 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="z-40 min-w-56 rounded-lg"
                side="bottom"
                align="end"
                sideOffset={4}
              >
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => handleMenuItemClick('account')}
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                {customMenuItems && (
                  <>
                    <DropdownMenuSeparator />
                    {customMenuItems}
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleMenuItemClick('logout')}>
                  <LogOutIcon className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
}
